import { isChargerSelectable } from '@/components/points/ChargerStatusBadge';
import type { StationDetailOut } from '@/api/stations';

/** Conectores livres/total somando todos os carregadores da estação. */
export function countAvailableConnectors(station: StationDetailOut): {
  available: number;
  total: number;
} {
  let available = 0;
  let total = 0;
  for (const charger of station.chargers) {
    for (const conn of charger.connectors) {
      total += 1;
      if (isChargerSelectable(conn.state)) available += 1;
    }
  }
  return { available, total };
}

/** Estados que significam "recarga impossível por queda/falha do equipamento".
 * NÃO inclui UNAVAILABLE (= em uso por outra sessão/tenant) nem UNKNOWN
 * (ambíguo — melhor cinza de "ocupado" que um falso "fora do ar"). `null` é o
 * fallback do backend quando o Hub não responde pela estação. */
const OFFLINE_STATES = new Set(['OFFLINE', 'FAULTED']);

/** Estação "fora do ar": tem conector e NENHUM deles reporta estado
 * operacional — todos em OFFLINE/FAULTED ou sem comunicação (null). Um único
 * conector vivo (livre, carregando ou mesmo ocupado) já descarta o rótulo. */
export function isStationOffline(station: StationDetailOut): boolean {
  let total = 0;
  for (const charger of station.chargers) {
    for (const conn of charger.connectors) {
      total += 1;
      const s = conn.state?.toUpperCase();
      if (s != null && !OFFLINE_STATES.has(s)) return false;
    }
  }
  return total > 0;
}

/** Menor tarifa R$/kWh entre os carregadores da estação (null = nenhuma
 * publicada). Mínimo, não "primeira não-nula": estação com tarifa de
 * operador por ponto pode ter valores divergentes entre chargers, e no
 * peek um valor acima do menor real afastaria o cliente à toa. */
export function stationTariff(station: StationDetailOut): string | null {
  let min: string | null = null;
  for (const charger of station.chargers) {
    const t = charger.tariff_per_kwh_brl;
    if (t !== null && (min === null || Number(t) < Number(min))) min = t;
  }
  return min;
}

/** Maior potência (kW) entre todos os conectores da estação. */
export function peakKw(station: StationDetailOut): number {
  let peak = 0;
  for (const charger of station.chargers) {
    for (const conn of charger.connectors) {
      const v = Number(conn.max_power_kw);
      if (v > peak) peak = v;
    }
  }
  return peak;
}
